import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import PropTypes from 'prop-types';
import { toLonLat } from 'ol/proj';
import { followModeActions, FollowModeEnum } from '../../../entities/map/followMode/redux/followMode.slice';
import { useAppDispatch, useAppSelector } from '../../../app/store';
import useTimeZone from '../../../entities/timeZone/hooks/useTimeZone';
import VehicleStopSpeedWidget from '../../../widgets/vehicles/VehicleStopSpeedWidget/VehicleStopSpeedWidget';
import VehicleHistoryToolbarWidget from '../../../widgets/vehicles/VehicleHistoryToolbarWidget/VehicleHistoryToolbarWidget';
import Container from './styles/Container';
import { useLazyFindGeoZoneByCoordsQuery } from '../../../entities/geoZones/geoZone/redux/geoZone.api';
import { parsePolygonWHoles } from '../../../shared/geo-map/lib';
import mapNames from '../../../entities/map/constants';
import reducerPath from '../../../app/reducerPath';
import VehicleHistoryIntervalWidget from '../../../widgets/vehicles/VehicleHistoryIntervalWidget/VehicleHistoryIntervalWidget';
import VehiclePlayerWidget from '../../../widgets/vehicles/VehiclePlayerWidget/VehiclePlayerWidget';
import Panel from './styles/Panel';
import MapBlock from './styles/MapBlock';
import RightSide from './styles/RightSide';
import DivisionVehicleMapWidget from '../../../widgets/vehicles/DivisionVehicleMapWidget/DivisionVehicleMapWidget';
import {
  useDivisionVehicleHistoryData,
  useVehicleHistoryDates,
  useVehicleHistoryExtent,
  useVehicleHistoryGeoZones,
  useVehicleHistoryPlayback,
} from '../../../entities/vehicles/hooks';
import { getExtentCoordsAroundPoint, getFormattedLastDate } from '../../../entities/vehicles/utils';
import { getLastDate } from '../../../entities/vehicles/utils/dateUtils';
import isValidDate from '../../../shared/utils/isValidDate';

// Начальный масштаб
export const zoom = 15;

// Отображает вкладку "Карта" с картой и маршрутами ТС подразделения
function DivisionHistoryPanel({ divisionId, trackPoints, dataVehiclesResource, getTrackPointsResourceParams }) {
  const [markers, setMarkers] = useState([]);
  const [enableGeozoneNames, setEnableGeozoneNames] = useState(true);
  const [enableMapRoutes, setEnableMapRoutes] = useState(true);
  const [speedLimit, setSpeedLimit] = useState(null);
  const [stopTime, setStopTime] = useState(null);
  const [disableExternalExtent, setDisableExternalExtent] = useState(false);

  const mapName = mapNames.vehicleStory;
  const currentZoom = useAppSelector((state) => state[`${reducerPath.positionMap}`][mapName])?.zoom;
  const isNeedHideGozoneNames = currentZoom && currentZoom < 12;

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(followModeActions.changeMode({ [FollowModeEnum.isActive]: true }));
    setDisableExternalExtent(false);
  }, [dispatch, divisionId]);

  const [findGeoZoneByCoords] = useLazyFindGeoZoneByCoordsQuery();

  const timeZone = useTimeZone();

  // Управление периодами истории подразделения
  const { dateStart, setDateStart, dateFinish, setDateFinish, intervalOption, setIntervalOption, getCustomHistory } =
    useVehicleHistoryDates(timeZone, getTrackPointsResourceParams, divisionId);

  // Данные истории подразделения: структурированные по vehicleId
  const { vehicleTracksMap, vehiclesInfo, allCoords } = useDivisionVehicleHistoryData(
    trackPoints,
    dataVehiclesResource,
  );

  // Управление воспроизведением
  const {
    coordsState,
    setCoordsState,
    runRec,
    speedRec,
    setSpeedRec,
    positionHistory,
    setPositionHistory,
    handleStart: playbackHandleStart,
    handleStop: playbackHandleStop,
  } = useVehicleHistoryPlayback(allCoords, divisionId);

  // Масштаб и экстент карты
  const { extent, changeExtent } = useVehicleHistoryExtent(allCoords, disableExternalExtent);

  // Геозоны
  const { geoZones, setGeoZones, isSearchGeozoneActive, setIsSearchGeozoneActive } =
    useVehicleHistoryGeoZones(allCoords);

  useEffect(() => {
    setMarkers([]);
  }, [divisionId]);

  // Обработчики для воспроизведения
  const handleStart = () => {
    if (!disableExternalExtent) {
      setCoordsState([allCoords[0]]);
    }
    setDisableExternalExtent(true);
    playbackHandleStart();
  };

  const handleStop = () => {
    playbackHandleStop();
  };

  const handleCurrentPosition = () => {
    handleStop();
    setDisableExternalExtent(false);
    changeExtent(getExtentCoordsAroundPoint(coordsState.length ? coordsState : allCoords));
  };

  const handleClickMap = useCallback(
    async (_, clickedFeature, eventCoords) => {
      if (!isSearchGeozoneActive) return;

      const [lon, lat] = toLonLat(eventCoords);
      const findedGeozonesData = await findGeoZoneByCoords({ lat, lon }).unwrap();

      setGeoZones((prev) => {
        if (clickedFeature) {
          const zoneId = clickedFeature.get('zoneId');
          if (prev.some((geo) => geo.geozoneId === zoneId)) {
            return prev.filter((geo) => geo.geozoneId !== zoneId);
          }
        }

        const newGeozones = findedGeozonesData?.data?.filter(
          (found) => !prev.some((existing) => existing.geozoneId === found.geozoneId),
        );

        if (newGeozones.length) {
          return [...prev, { ...newGeozones[0], polygon: parsePolygonWHoles(newGeozones[0].area) }];
        }

        return prev;
      });
    },
    [isSearchGeozoneActive, findGeoZoneByCoords],
  );

  const handleSaveMarker = () => {
    setMarkers((prev) => [
      ...prev,
      coordsState.map((point) => ({
        longitude: point.longitude,
        latitude: point.latitude,
      })).length - 1,
    ]);
  };

  const lastDate = getLastDate(allCoords, coordsState);
  const formattedLastDate = getFormattedLastDate(allCoords, coordsState, timeZone);

  return (
    <Container>
      <VehicleHistoryToolbarWidget
        isSearchGeozoneActive={isSearchGeozoneActive}
        onClickSearchGeozone={() => setIsSearchGeozoneActive((prev) => !prev)}
      />
      <Panel>
        {allCoords?.length ? (
          <DivisionVehicleMapWidget
            mapName={mapName}
            zoom={zoom}
            extent={extent}
            isAnimateOnExtent={!disableExternalExtent}
            vehicleTracksMap={vehicleTracksMap}
            speedLimit={speedLimit}
            stopTime={stopTime}
            geoZones={geoZones}
            enableGeozoneNames={enableGeozoneNames}
            enableMapRoutes={enableMapRoutes}
            isNeedHideGozoneNames={isNeedHideGozoneNames}
            vehicles={dataVehiclesResource?.data}
            vehiclesInfo={vehiclesInfo}
            coordsState={coordsState}
            markers={markers}
            handleClickMap={handleClickMap}
            currentDate={lastDate}
          />
        ) : (
          <MapBlock>Нет данных для отображения.</MapBlock>
        )}
        <RightSide>
          <VehicleHistoryIntervalWidget
            intervalOption={intervalOption}
            setIntervalOption={setIntervalOption}
            setDisableExternalExtent={setDisableExternalExtent}
            dateStart={dateStart}
            setDateStart={setDateStart}
            dateFinish={dateFinish}
            setDateFinish={setDateFinish}
            isValidDate={isValidDate}
            getCustomHistory={getCustomHistory}
            handleCurrentPosition={handleCurrentPosition}
          />

          <FormControlLabel
            control={<Checkbox checked={enableMapRoutes} onChange={(e) => setEnableMapRoutes(e.target.checked)} />}
            label="Показ треков"
          />

          <FormControlLabel
            control={
              <Checkbox checked={enableGeozoneNames} onChange={(e) => setEnableGeozoneNames(e.target.checked)} />
            }
            label="Показ названий геозон"
          />

          <VehicleStopSpeedWidget
            speedLimit={speedLimit}
            setSpeedLimit={setSpeedLimit}
            stopTime={stopTime}
            setStopTime={setStopTime}
          />

          <VehiclePlayerWidget
            runRec={runRec}
            handleStart={handleStart}
            handleStop={handleStop}
            speedRec={speedRec}
            setSpeedRec={setSpeedRec}
            coordsState={coordsState}
            coords={allCoords}
            positionHistory={positionHistory}
            setPositionHistory={setPositionHistory}
            onSaveMarker={handleSaveMarker}
            formattedLastDate={formattedLastDate}
            hideSpeed
          />
        </RightSide>
      </Panel>
    </Container>
  );
}

DivisionHistoryPanel.propTypes = {
  divisionId: PropTypes.number.isRequired,
  trackPoints: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dataVehiclesResource: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  getTrackPointsResourceParams: PropTypes.func.isRequired,
};

DivisionHistoryPanel.defaultProps = {};

export default DivisionHistoryPanel;
